import React, { useState, useEffect } from "react";
import { Button, PageHeader, Plus, Tabs } from "@opendate-app/setlist";

import { useCampaignsContext } from "./CampaignsContainer";
import CampaignIndexDatatable from "./CampaignIndexDatatable";
import CreateCampaignPane from "./CreateCampaignPane";
import MarketingVenueSelector from "../shared/MarketingVenueSelector";
import SearchBar from "../shared/SearchBar";
import { writeListParams, rememberListUrl } from "../shared/listUrlState";
import { CAMPAIGN_LIST_DEFAULTS as LIST_DEFAULTS, CAMPAIGN_STATES, campaignsSourceUrl, readCampaignListParams } from "./campaignsSource";
import CountBadge from "../../shared/components/CountBadge";

const STATE_LABELS = { all: "View all", active: "Active", paused: "Paused", created: "Created" };
const STATE_TABS = CAMPAIGN_STATES.map((value) => ({ value, label: STATE_LABELS[value] }));

const cardStyle = {
  marginTop: 24,
  borderRadius: "12px",
  boxShadow: "var(--sl-shadow-xs)",
  border: "1px solid var(--sl-border-secondary)",
  background: "var(--sl-bg-primary)",
};

const CampaignIndex = () => {
  const {
    team,
    teamMembership,
    enforceMarketingVenueAccessControl,
    csrfToken,
    customCampaigns,
    customCampaignTemplates,
    aiAutomationBuilder,
    venueOwnerships,
    dataSource,
    dataTableSource,
    setDataTableSource,
    refreshDataTable,
    campaignCount,
  } = useCampaignsContext();
  const listPath = `/teams/${team.id}/campaigns`;
  const [initial] = useState(readCampaignListParams);
  const [activeFilter, setActiveFilter] = useState(initial.tab);
  const [searchTerm, setSearchTerm] = useState(initial.q);
  const [isCreatePaneOpen, setIsCreatePaneOpen] = useState(false);
  const updateDataSource = (state, search) => {
    setDataTableSource(campaignsSourceUrl(dataSource, state, search));
  };

  useEffect(() => {
    rememberListUrl(listPath);
  }, []);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSearchTerm("");
    updateDataSource(filter, "");
    writeListParams(listPath, { tab: filter, q: "" }, LIST_DEFAULTS);
  };

  const handleSearchChange = (search) => {
    setSearchTerm(search);
    updateDataSource(activeFilter, search);
    writeListParams(listPath, { tab: activeFilter, q: search }, LIST_DEFAULTS);
  };

  return (
    <div>
      <PageHeader
        title={(
          <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
            Automations
            <CountBadge count={campaignCount} />
          </span>
        )}
        actions={(
          <>
            {enforceMarketingVenueAccessControl && (
              <MarketingVenueSelector
                teamId={team.id}
                teamMembership={teamMembership}
                csrfToken={csrfToken}
                onChange={refreshDataTable}
              />
            )}
            <Button variant="primary" icon={Plus} onClick={() => setIsCreatePaneOpen(true)}>
              Create Automation
            </Button>
          </>
        )}
      />
      <div className="overflow-hidden" style={cardStyle}>
        <div className="px-4 py-3 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 12 }}>
          <div style={{ maxWidth: "100%", overflowX: "auto" }}>
            <div style={{ width: "max-content" }}>
              <Tabs
                variant="button-border"
                size="sm"
                collapseBelow={0}
                items={STATE_TABS}
                value={activeFilter}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <SearchBar
            key={activeFilter}
            defaultValue={searchTerm}
            placeholder="Search automations"
            onDebouncedChange={handleSearchChange}
          />
        </div>
        <CampaignIndexDatatable key={dataTableSource} />
        <CreateCampaignPane
          isOpen={isCreatePaneOpen}
          onClose={() => setIsCreatePaneOpen(false)}
          customCampaignTemplates={customCampaignTemplates}
          aiAutomationBuilder={aiAutomationBuilder}
          customCampaigns={customCampaigns}
          team={team}
          teamMembership={teamMembership}
          csrfToken={csrfToken}
          enforceMarketingVenueAccessControl={enforceMarketingVenueAccessControl}
          venueOwnerships={venueOwnerships}
        />
      </div>
    </div>
  );
};

export default CampaignIndex;
